import { AxiosResponse } from "axios";

/**
 * @description - Initiates a file download from the given response
 * @param {object} response - The response object from the axios request
 * @param {string} fileName - (optional) The name of the downloaded file (defaults to the file name from the response header)
 * @returns {Promise} - A promise that resolves when the file download is initiated
 */
export default async function downloadFile(response: AxiosResponse, fileName?: string): Promise<void> {
  if (!response) throw new Error('No response provided');

  const fileType = response.headers['content-type'] as string | undefined;
  const contentDisposition = response.request.getResponseHeader('Content-Disposition') as string;
  const matches = new RegExp(/(?<=filename(?:=|\*=(?:[\w-]+'')))["']?(?<filename>[^"';\n]+)["']?/).exec(contentDisposition);
  const blob = new Blob([response.data], { type: fileType });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute("href", url);
  if (fileName) {
    link.setAttribute("download", fileName);
  } else if (matches?.groups?.filename) {
    link.setAttribute("download", matches.groups?.filename);
  }
  link.setAttribute("target", "_blank");
  link.click();
  // An object URL holds its bytes until it is revoked or the page unloads, so every download kept a
  // copy of its file for the life of the page (#5992). Not revoked at once: with no file name the
  // link opens the blob in a new tab, which reads the URL after this returns.
  globalThis.setTimeout(() => globalThis.URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
}

/** How long a download's object URL outlives the click that used it. */
export const OBJECT_URL_LIFETIME_MS = 10_000;
